import { useTranslation } from 'react-i18next';

function ServiceCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#E8A020]/40 transition-all duration-300 group cursor-pointer">
      <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-4 group-hover:bg-[#E8A020] transition-colors duration-300">
        <i className={`${icon} text-2xl text-[#E8A020] group-hover:text-white transition-colors duration-300`} />
      </div>
      <h3 className="font-bold text-[#1A2332] text-base mb-2">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Services() {
  const { t } = useTranslation();

  const electricalServices = [
    { icon: 'ri-flashlight-line', title: t('svc_mv_title'), desc: t('svc_mv_desc') },
    { icon: 'ri-lightbulb-line', title: t('svc_lv_title'), desc: t('svc_lv_desc') },
    { icon: 'ri-shield-check-line', title: t('svc_weak_title'), desc: t('svc_weak_desc') },
    { icon: 'ri-cpu-line', title: t('svc_auto_title'), desc: t('svc_auto_desc') },
  ];

  const constructionServices = [
    { icon: 'ri-building-2-line', title: t('svc_rough_title'), desc: t('svc_rough_desc') },
    { icon: 'ri-paint-brush-line', title: t('svc_finish_title'), desc: t('svc_finish_desc') },
    { icon: 'ri-landscape-line', title: t('svc_ext_title'), desc: t('svc_ext_desc') },
    { icon: 'ri-bar-chart-grouped-line', title: t('svc_pm_title'), desc: t('svc_pm_desc') },
  ];

  return (
    <section className="py-24 bg-[#F8F9FA]" id="services">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase">{t('services_label')}</span>
          <h2 className="text-3xl md:text-4xl font-black text-[#1A2332] mt-3 mb-4">
            {t('services_title')}
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            {t('services_desc')}
          </p>
        </div>

        {/* Electrical */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1A2332] rounded-lg">
              <i className="ri-flashlight-fill text-[#E8A020] text-lg" />
            </div>
            <h3 className="text-xl font-black text-[#1A2332] uppercase tracking-wide">{t('services_electrical_title')}</h3>
            <div className="flex-1 h-px bg-gray-200" />
            <a href="/electrical" className="text-sm text-[#E8A020] font-semibold hover:underline cursor-pointer whitespace-nowrap">
              {t('services_view_all')} <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {electricalServices.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>

        {/* Construction */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1A2332] rounded-lg">
              <i className="ri-building-fill text-[#E8A020] text-lg" />
            </div>
            <h3 className="text-xl font-black text-[#1A2332] uppercase tracking-wide">{t('services_construction_title')}</h3>
            <div className="flex-1 h-px bg-gray-200" />
            <a href="/construction" className="text-sm text-[#E8A020] font-semibold hover:underline cursor-pointer whitespace-nowrap">
              {t('services_view_all')} <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {constructionServices.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
